import * as React from "react";
import { Form, Checkbox, CheckboxProps } from "semantic-ui-react";

import { QuestionProps, AnsweredQuestion } from "../../model";
import { isQuestionDisabled } from "../../questionnaireConfig/models";

import { QuestionWrapper } from "./QuestionWrapper";

interface MultiCheckboxProps {
    value: string;
    label: string;
    questionId?: string;
    answer?: string[];
    onChange?: (event: React.FormEvent<HTMLInputElement>, props: CheckboxProps) => void;
}

interface MultiCheckboxQuestionProps {
    options?: { [key: string]: string };
}

const MultiCheckbox: React.SFC<MultiCheckboxProps> = ({ value, label, questionId, answer, onChange }) => {
    return (
        <Form.Field key={value}>
            <Checkbox
                label={label}
                name={`question-${questionId}`}
                id={`question-${questionId}-${value}`}
                value={value}
                checked={!!(answer && answer.indexOf(value) > -1)}
                onChange={onChange}
            />
        </Form.Field>
    );
};

class MultiCheckboxQuestion extends React.Component<QuestionProps & MultiCheckboxQuestionProps> {
    constructor(props: QuestionProps) {
        super(props);

        this.onChange = this.onChange.bind(this);
    }

    public render() {
        if (this.props.hidden || isQuestionDisabled(this.props.questionConfig)) {
            return null;
        }

        return (
            <QuestionWrapper
                question={this.props.questionConfig.questionText}
                inline={this.props.inline}
                mandatory={this.props.mandatory}
                hideDivider={this.props.hideDivider}
                questionClassName={this.props.questionClassName}
                readonly={this.props.readonly}
                removable={{
                    ...this.props.removable,
                    questionId: this.props.questionConfig.id,
                    isRemovedByUser: this.props.isRemovedByUser
                }}
                outdated={{
                    ...this.props.outdated,
                    questionId: this.props.questionConfig.id
                }}
            >
                {this.renderValue()}
            </QuestionWrapper>
        );
    }

    private renderValue() {
        if (this.props.readonly) {
            if (this.props.options) {
                return <ul className={"question-ul"}>{(this.props.answer as string[] || []).map(a => <li key={a}>{this.props.options[a]}</li>)}</ul>;
            }
            return <ul className={"question-ul"}>{(this.props.answer as string[] || []).map(a => <li key={a}>{a}</li>)}</ul>;
        }
        const { questionConfig, children } = this.props;
        const answer = this.props.answer as string[];
        const onChange = this.onChange;
        return React.Children.map(children, (child) => {
            const multiCheckbox = child as React.ReactElement<MultiCheckboxProps>;
            const newProps: MultiCheckboxProps = { ...multiCheckbox.props, questionId: questionConfig.id, answer, onChange };
            return React.cloneElement(multiCheckbox, newProps);
        });
    }

    private onChange(event: React.MouseEvent<HTMLInputElement>, props: CheckboxProps) {
        if (isQuestionDisabled(this.props.questionConfig)) {
            return;
        }

        const { questionConfig, updateQuestion } = this.props;
        let newAnswer;
        if (props.checked) {
            newAnswer = [...(this.props.answer as string[] || []), props.value as string];
        } else {
            newAnswer = (this.props.answer as string[]).filter(a => a !== props.value as string);
        }

        const userFriendlyAnswer = this.props.options
            ? newAnswer.filter(na => na in this.props.options).map(na => this.props.options[na])
            : newAnswer;
        const newQuestion: AnsweredQuestion = {
            question: questionConfig.questionText,
            answer: newAnswer,
            userFriendlyAnswer,
            isValid: true
        };
        updateQuestion(newQuestion);
        if (this.props.onAnswerChange) {
            this.props.onAnswerChange(newQuestion.answer);
        }
    }
}

export { MultiCheckboxQuestion, MultiCheckbox };
